import store from '@ps/store'
import { findByUuid } from '@ps/bridge/qc/node/utils'
import qc from 'qc'
import _ from 'lodash'
import nodePool from '@ps/bridge/qc/node-pool'
import { addChildAt } from '@ps/bridge/qc/node'
import { TrackGroupInsertResult } from '@ps/store/interactive-video/timeline/utiles/track-group-insert'
import {
  ivTimeline2NodeExecuteOperation,
  ivTimeline2NodeUndoOperation,
} from '@ps/store/interactive-video/timeline/action'
import { NodeAction } from '@ps/store/interactive-video/brain/types'
import { EventBus, Event } from '@ps/utils/event-bus'
import { addEventItem } from '@ps/store/interactive-video/event/event'
import { EventType, VpMethods } from '@ps/bridge/qc/vp/config'
import { getJumpSceneUuidByNodeUuid } from '@ps/store/interactive-video/brain/brain'
import { getSceneNodeByNode } from '@ps/bridge/qc/base'
import { hasAction } from '@ps/store/interactive-video/timeline/timeline'

type removeNodeInfo = {
  node: qc.Node
  parent: qc.Node
  index: number
}

export type AddIvSegmentActionValue = {
  newValue: {
    node: qc.Node
    parent: qc.Node
    index: number
  }
  oldValue: {
    node: qc.Node
    activeUuid: string
  }
}
export const getAddIvSegmentAction = (value: AddIvSegmentActionValue) => {
  return {
    execute: ({ newValue }: AddIvSegmentActionValue) => {
      const { node, parent, index } = newValue
      if (!findByUuid(node.uuid)) {
        addChildAt({
          node,
          parent,
          index,
        })
      }
      store.dispatch('workspace/game/updateActiveNode', {
        node,
        record: false,
      })
    },
    undo: ({ oldValue }: AddIvSegmentActionValue) => {
      const { node, activeUuid } = oldValue
      node.removeSelf()

      // 如果undo的时候记录了要激活的节点，则激活该节点
      const activeNode = nodePool.find(activeUuid)
      if (activeNode) {
        store.dispatch('workspace/game/updateActiveNode', {
          node: activeNode,
          record: false,
        })
      } else {
        store.dispatch('workspace/game/clearActiveNode', {
          record: false,
        })
      }
    },
    value,
    methodName: 'getAddIvSegmentAction',
  }
}

const actionHandler = (
  spotAction: NodeAction,
  nodeActionInfo: removeNodeInfo[],
  backUpNode?: qc.Node,
) => {
  if (spotAction.action === 'delete') {
    spotAction.spots.forEach((node) => {
      store.dispatch('workspace/game/visualProgram/removeNode', {
        uuid: node.uuid,
      })
      node.removeSelf()
    })
    // 删除节点时提供兜底激活节点
    if (backUpNode) {
      store.dispatch(
        'workspace/game/updateActiveNode',
        { node: backUpNode, record: false },
        { root: true },
      )
    }
  } else if (spotAction.action === 'add') {
    spotAction.spots.forEach((node, index) => {
      if (nodeActionInfo[index]) {
        addChildAt({
          node,
          parent: nodeActionInfo[index].parent,
          index: nodeActionInfo[index].index,
        })
        const jumpSceneId = getJumpSceneUuidByNodeUuid(
          nodeActionInfo[index].node.uuid,
        )
        if (jumpSceneId) {
          const sceneNode = getSceneNodeByNode(nodeActionInfo[index].node)
          // 防止重复添加事件
          if (
            !hasAction(
              sceneNode,
              nodeActionInfo[index].node,
              VpMethods.vpSwitchScene,
            )
          ) {
            addEventItem({
              conditionNode: nodeActionInfo[index].node,
              actionNode: nodeActionInfo[index].node,
              handCmd: EventType.down,
              action: VpMethods.vpSwitchScene,
              param: {
                jumpSceneId: jumpSceneId,
              },
            })
          }
        }
      }
    })
  }
}

export type SwitchIvSegmentActionValue = {
  newValue: {
    groupInsertResults: TrackGroupInsertResult[]
    nodeActionInfo: removeNodeInfo[]
    spotAction: NodeAction
    linkNode: qc.Node
  }
  oldValue: {
    groupInsertResults: TrackGroupInsertResult[]
    oldNodeData: removeNodeInfo[]
    nodeActionInfo: removeNodeInfo[]
    spotAction: NodeAction
    linkNode: qc.Node
  }
}
export const getSwitchIvSegmentAction = (value: SwitchIvSegmentActionValue) => {
  return {
    execute: ({ newValue }: SwitchIvSegmentActionValue) => {
      const { groupInsertResults, spotAction, nodeActionInfo, linkNode } =
        newValue
      ivTimeline2NodeExecuteOperation(groupInsertResults)
      actionHandler(spotAction, nodeActionInfo, linkNode)
    },
    undo: ({ oldValue }: SwitchIvSegmentActionValue) => {
      const {
        groupInsertResults,
        spotAction,
        nodeActionInfo,
        oldNodeData,
        linkNode,
      } = oldValue
      ivTimeline2NodeUndoOperation(groupInsertResults, oldNodeData)
      actionHandler(spotAction, nodeActionInfo, linkNode)
    },
    value,
    methodName: 'getSwitchIvSegmentAction',
  }
}

export type AddBrainItemActionValue = {
  dependAction: any
  newValue: {
    nodeAction: NodeAction
    nodeActionInfo: removeNodeInfo[]
  }
  oldValue: {
    nodeAction: NodeAction
    nodeActionInfo: removeNodeInfo[]
  }
}

export const getAddBrainItemAction = (value: AddBrainItemActionValue) => {
  return {
    execute: ({ newValue, dependAction }: AddBrainItemActionValue) => {
      const { nodeAction, nodeActionInfo } = newValue
      dependAction.execute(dependAction.value)
      actionHandler(nodeAction, nodeActionInfo)
      EventBus.$emit(Event.IV_BRAIN_CHANGE)
    },
    undo: ({ oldValue, dependAction }: AddBrainItemActionValue) => {
      const { nodeAction, nodeActionInfo } = oldValue
      dependAction.undo(dependAction.value)
      actionHandler(nodeAction, nodeActionInfo)
      EventBus.$emit(Event.IV_BRAIN_CHANGE)
    },
    value,
    methodName: 'getAddBrainItemAction',
  }
}

export type DeleteBrainItemActionValue = {
  dependAction: any
  newValue: {
    nodeAction: NodeAction
    nodeActionInfo: removeNodeInfo[]
  }
  oldValue: {
    nodeAction: NodeAction
    nodeActionInfo: removeNodeInfo[]
  }
}
export const getDeleteBrainItemAction = (value: DeleteBrainItemActionValue) => {
  return {
    execute: async ({ newValue, dependAction }: DeleteBrainItemActionValue) => {
      const { nodeAction, nodeActionInfo } = newValue
      actionHandler(nodeAction, nodeActionInfo)
      await dependAction.execute(dependAction.value)
      EventBus.$emit(Event.IV_BRAIN_CHANGE)
    },
    undo: async ({ oldValue, dependAction }: DeleteBrainItemActionValue) => {
      const { nodeAction, nodeActionInfo } = oldValue
      await dependAction.undo(dependAction.value)
      actionHandler(nodeAction, nodeActionInfo)
      setTimeout(() => {
        EventBus.$emit(Event.IV_BRAIN_CHANGE)
      }, 0)
    },
    value,
    methodName: 'getDeleteBrainItemAction',
  }
}

export type PasteBrainItemActionValue = {
  newValue: {
    nodeAction: NodeAction
    nodeActionInfo: removeNodeInfo[]
  }
  oldValue: {
    nodeAction: NodeAction
    nodeActionInfo: removeNodeInfo[]
  }
}
export const getPasteBrainItemAction = (value: PasteBrainItemActionValue) => {
  return {
    execute: ({ newValue }: PasteBrainItemActionValue) => {
      const { nodeAction, nodeActionInfo } = newValue
      actionHandler(nodeAction, nodeActionInfo)
      EventBus.$emit(Event.IV_BRAIN_CHANGE)
      EventBus.$emit(Event.SILENT_SAVE)
      store.commit('workspace/unlockPage')
    },
    undo: ({ oldValue }: PasteBrainItemActionValue) => {
      // const { node, activeUuid } = oldValue
      // node.removeSelf()
      // // 如果undo的时候记录了要激活的节点，则激活该节点
      // const activeNode = nodePool.find(activeUuid)
      // if (activeNode) {
      //   store.dispatch('workspace/game/updateActiveNode', {
      //     node: activeNode,
      //     record: false,
      //   })
      // } else {
      //   store.dispatch('workspace/game/clearActiveNode', {
      //     record: false,
      //   })
      // }
    },
    value,
    methodName: 'getPasteBrainItemAction',
  }
}
